/**
 * Strukturelle Wörterbücher für den Bestand-Konverter (aus der Korpus-Analyse
 * von 3150 echten Bestand-Zellen abgeleitet). Schreibvarianten/Tippfehler →
 * kanonische Form.
 *
 * Die ARTEN-Liste liegt separat in bestand-vocabulary.seed.ts (auto-generiert;
 * soll in Produktion aus der bpweb-listen-DB kommen).
 */
import { SEA_BAUMARTEN, ART_ALIAS, SeaArt } from './bestand-vocabulary.seed';
import { BestandEbene } from './bestand-model';

export { SEA_BAUMARTEN, ART_ALIAS };
export type { SeaArt };

/** Umlaute falten (für tolerantes Matching). */
export function fold(s: string): string {
	return s
		.replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue')
		.replace(/Ä/g, 'Ae').replace(/Ö/g, 'Oe').replace(/Ü/g, 'Ue')
		.replace(/ß/g, 'ss');
}

/** Normalform: gefaltet, klein, ohne Punkte/Leerzeichen. */
export function nf(s: string): string {
	return fold(s).toLowerCase().replace(/\./g, '').replace(/ /g, '');
}

/** Bestandesschluss: Schreibvariante → kanonisch. */
export const SCHLUSS: Readonly<Record<string, string>> = {
	ge: 'geschl.', geschl: 'geschl.', geschlossen: 'geschl.', geschlossener: 'geschl.', gesvhl: 'geschl.', grschl: 'geschl.', gschl: 'geschl.',
	lo: 'locker', locker: 'locker', kocker: 'locker', licker: 'locker', liocker: 'locker',
	gd: 'gedr.', gedr: 'gedr.', ged: 'gedr.',
	li: 'licht', licht: 'licht', lichtes: 'licht', lli: 'licht', ll: 'licht', aufgelichtet: 'licht', aufgelichtetes: 'licht', lucht: 'licht',
	rd: 'räumdig', raeumdig: 'räumdig', raeumig: 'räumdig', raezmdig: 'räumdig', razmdig: 'räumdig', rae: 'räumdig', raeu: 'räumdig',
	lue: 'lückig', lueckig: 'lückig',
	gedraengt: 'gedr.', gedraengtes: 'gedr.',
};

/** Stufen-Qualifizierer: Schreibvariante → kanonisch (schw./mtl./st.). */
export const QUAL: Readonly<Record<string, string>> = {
	sw: 'schw.', schw: 'schw.', svhw: 'schw.', sch: 'schw.', schwaches: 'schw.', schwach: 'schw.',
	stark: 'st.', starkes: 'st.', mtl: 'mtl.', m: 'mtl.', mittl: 'mtl.', mittel: 'mtl.', mittleres: 'mtl.', at: 'alt',
};

/** Stufe (interne Marke; SEA-Render erfolgt im Parser): Variante → Marke. */
export const STUFE: Readonly<Record<string, string>> = {
	bh: 'Bh.', bmh: 'Bh.', baumh: 'Bh.',
	sth: 'St.', stgh: 'St.', stangh: 'St.', ststgh: 'St.',
	// Anwuchs und Aufwuchs sind ZWEI Stufen (Liste „Bestandsbeschreibungen":
	// ANW bzw. AUFW). Bis 29.08.2026 warf der Konverter beide auf ein Kürzel
	// zusammen — fachlich falsch, siehe Rückmeldung aus der Anwendung.
	anw: 'Anw.', anwuchs: 'Anw.',
	aw: 'Aw.', aufw: 'Aw.', aufwuchs: 'Aw.',
	jw: 'Jgw.', jgw: 'Jgw.', jghw: 'Jgw.', juw: 'Jgw.', jungw: 'Jgw.', jungwuchs: 'Jgw.',
	dckg: 'Dckg.', dickung: 'Dckg.', dg: 'Dckg.',
	bl: 'Blöße', bloesse: 'Blöße', blsse: 'Blöße', bloe: 'Blöße',
	ugl: 'UGL', ungleichaltrig: 'UGL',
	// Baumholz-Stärken als eigene Marken. Eingabe-Schreibvarianten UND die
	// zuvor ausgegebenen Kürzel (SWBH/MIBH/STBH) landen auf derselben Marke,
	// damit alte Texte weiter gelesen werden und der Konverter idempotent bleibt.
	schbmh: 'schw.Bh.', schwbmh: 'schw.Bh.', sbmh: 'schw.Bh.', swbh: 'schw.Bh.',
	mtlbmh: 'mtl.Bh.', mbmh: 'mtl.Bh.', mibh: 'mtl.Bh.',
	stkbmh: 'st.Bh.', stbmh: 'st.Bh.', stbh: 'st.Bh.',
};

/**
 * Baumholz-Wuchsklasse: Qualifizierer → Kürzel aus der Liste
 * „Bestandsbeschreibungen" (SEA).
 */
export const SEA_BH: Readonly<Record<string, string>> = {
	'schw.': 'SCHBMH', 'mtl.': 'MTLBMH', 'st.': 'STKBMH',
};

/**
 * Stufen-Marke → Kürzel aus der Liste „Bestandsbeschreibungen" (SEA).
 *
 * Bis 29.08.2026 schrieb der Konverter hier eigene Kürzel (STH/AW/JW/DG bzw.
 * SWBH/MIBH/STBH), die es in der Liste gar nicht gab — er widersprach also der
 * Listenverwaltung, obwohl er sich „SEA" nannte. Jetzt gilt die Liste.
 * `UGL` und `ÜH` stehen nicht in der Liste, sind aber Schicht-/Aufbau-Angaben
 * und keine Wuchsklassen — sie bleiben unverändert.
 */
export const SEA_STUFE: Readonly<Record<string, string>> = {
	'St.': 'STANGH', 'Anw.': 'ANW', 'Aw.': 'AUFW', 'Jgw.': 'JUW', 'Dckg.': 'DCKG',
	'schw.Bh.': 'SCHBMH', 'mtl.Bh.': 'MTLBMH', 'st.Bh.': 'STKBMH',
	'Blöße': 'Bloe', 'UGL': 'UGL', 'ÜH': 'ÜH',
};

/** Mischbestand-Platzhalter (werden wie eine "Art" geführt). */
export const MIX: Readonly<Record<string, string>> = {
	laubmisch: 'Laubmisch', laubmischbestand: 'Laubmisch', laub: 'Laubmisch', laubholz: 'Laubmisch',
	nadelmisch: 'Nadelmisch', nadelholz: 'Nadelmisch', nadel: 'Nadelmisch',
};

/** Baumart als ausgeschriebenes Wort → kanonischer Code (aus CouchDB-Prosa). */
export const SPECIES_WORD: Readonly<Record<string, string>> = {
	hasel: 'SHA', holunder: 'SHO', holu: 'SHO', weissdorn: 'EWD', traubenkirsche: 'GTK',
	eiche: 'EI', kiefer: 'GKI', aspe: 'AS', espe: 'AS', schlehe: 'SDO', schwarzdorn: 'SDO',
	bruchweide: 'BWE', robinie: 'RO', wacholder: 'GWA', ahorn: 'AHS', pappel: 'PAS',
	ulme: 'RUS', ruester: 'RUS', weide: 'WEB', linde: 'LIS', faulbaum: 'FLB', esche: 'GES',
	birke: 'GBI', erle: 'RER', roterle: 'RER', schwarzerle: 'RER', buche: 'RBU', rotbuche: 'RBU',
	hainbuche: 'HBU', fichte: 'GFI', douglasie: 'GDG', laerche: 'ELA', tanne: 'WTA',
	elsbeere: 'EL', rosskastanie: 'RK',
};

/**
 * Schicht-Marker → Schicht-Ebene. Reihenfolge der Ebenen siehe BestandEbene
 * (Überhalt, Baumschicht 1/2, Zwischenstand, Unterstand=Verjüngung).
 */
export const LAYER_MARK: Readonly<Record<string, BestandEbene>> = {
	// Überhalt (auch Oberstand)
	ueh: 'ueh', ueberhalt: 'ueh', ueberhaelter: 'ueh', uebh: 'ueh', ostd: 'ueh', oberstand: 'ueh',
	// Baumschicht 1 (Hauptstand) / 2
	b1: 'b1', baumschicht1: 'b1', bs1: 'b1',
	b2: 'b2', baumschicht2: 'b2', bs2: 'b2',
	// Zwischenstand
	zwstd: 'zwstd', zstd: 'zwstd', zwst: 'zwstd', zwatd: 'zwstd', zwytd: 'zwstd', zwsrd: 'zwstd',
	zwsd: 'zwstd', zs: 'zwstd', zwischenstand: 'zwstd',
	// Unterstand (= Verjüngung)
	ustd: 'ustd', ust: 'ustd', usd: 'ustd', utd: 'ustd', usrd: 'ustd', usts: 'ustd', istd: 'ustd',
	untsd: 'ustd', uatd: 'ustd', ustf: 'ustd', uytd: 'ustd', us: 'ustd', unterstand: 'ustd',
	verj: 'ustd', verjuengung: 'ustd', verjg: 'ustd',
};

/** Offenland-/Biotop-Bezeichnungen (kein Baumbestand). */
export const BIOTOPE: ReadonlySet<string> = new Set([
	'wiese', 'acker', 'wildacker', 'gruenland', 'bloesse', 'luecke', 'kahlschlag', 'kahlhieb',
	'windwurf', 'windwurfflaeche', 'waldrand', 'bestandesrand', 'grauweidengebuesch', 'weidengebuesch',
	'korbweidengebuesch', 'schlehengebuesch', 'hartriegelgebuesch', 'grauweide', 'grauweiden',
	'schilfroehricht', 'schilfried', 'schilf', 'schilfwiese', 'gebuesch', 'gewaesser', 'sukzession',
	'bulten', 'buelten', 'restbestand', 'restbestockung', 'erstaufforstung', 'kultur', 'grenze', 'rand',
	// landwirtschaftliches Offenland (aus CouchDB-Daten)
	'getreide', 'wintergetreide', 'sommergetreide', 'raps', 'mais', 'gerste', 'roggen', 'weizen', 'hafer',
	'erbsen', 'kartoffeln', 'ruebe', 'rueben', 'saatbeet', 'brache', 'oedland', 'baumfrei', 'park',
	'bruchweidengebuesch', 'weidengebuesch', 'bestandesgrenze', 'strauchschicht',
]);

/** Bekannte Prosa-Konnektoren (mindern Confidence leicht, blockieren nicht). */
export const CONNECT: ReadonlySet<string> = new Set([
	'einz', 'enz', 'eunz', 'mit', 'und', 'u', 'bis', 'nach', 'ehem', 'ehemals', 'vormals', 'im',
	'absterbend', 'abgestorben', 'abgestorb', 'abgest', 'abgaengig', 'abgaengiges', 'verbissen',
	'angrenzend', 'umgebend', 'dicht', 'dichter', 'dichtes', 'gemaehte', 'gemaeht', 'kurzgemaehte',
	'abgegraste', 'geraeumt', 'gefaellt', 'zerstoert', 'schlechtwuechsig', 'schlechtwuechsiges',
	'gute', 'frisch', 'stw', 'horstw', 'gruppenw', 'tw', 'in', 'an', 'am', 'auf', 'aus', 'von',
	'etwas', 'sehr', 'kurz', 'eben', 'stellenweise', 'insgesamt', 'einzeln', 'einzelne', 'einzelnen',
	'geschaedigt', 'wuechsig', 'schwachwuechsig', 'alt', 'alten', 'bzw',
]);

export interface SpeciesIndex {
	resolve(rawCode: string): { code: string; bekannt: boolean };
}

/**
 * Baut den Arten-Index aus der (ggf. DB-gelieferten) Artenliste + Aliassen.
 * `resolve` liefert den kanonischen SEA-Code und ob er bekannt ist.
 */
export function buildSpeciesIndex(
	arten: readonly SeaArt[] = SEA_BAUMARTEN,
	alias: Readonly<Record<string, string>> = ART_ALIAS,
): SpeciesIndex {
	const known = new Set(arten.map(a => a.kurz.toUpperCase()));
	return {
		resolve(rawCode: string) {
			let c = rawCode.toUpperCase();
			if (alias[c]) c = alias[c];
			if (known.has(c)) return { code: c, bekannt: true };
			return { code: rawCode, bekannt: false };
		},
	};
}
